import React, { useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

// The org's secret lähete inbox address with a copy button. Used in the empty
// state, above the list, and in the Features settings tab.
function InboxAddressCard({ compact = false }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const [copied, setCopied] = useState(false);
  const address = currentOrganization?.lahete_inbox_address;

  if (!address) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard unavailable (http / permissions) — user can still select the text
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: compact ? 1.75 : 2,
        py: compact ? 1.25 : 1.75,
        borderRadius: '10px',
      }}
    >
      <AlternateEmailIcon sx={{ color: 'secondary.main', flexShrink: 0, fontSize: compact ? 20 : 24 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '10.5px',
            fontWeight: 700,
            letterSpacing: '.07em',
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          {t('inboundDeliveries.inboxAddressLabel')}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: compact ? '13.5px' : '15px',
            mt: 0.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          data-testid="lahete-inbox-address"
        >
          {address}
        </Typography>
      </Box>
      <Button
        variant={copied ? 'contained' : 'outlined'}
        color={copied ? 'success' : 'primary'}
        size="small"
        startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
        onClick={handleCopy}
        sx={{ flexShrink: 0 }}
        data-testid="lahete-copy-address"
      >
        {copied ? t('inboundDeliveries.copied') : t('inboundDeliveries.copy')}
      </Button>
    </Paper>
  );
}

export default InboxAddressCard;
